import React, { FC, useMemo } from 'react';

import pluralize from 'pluralize';

import { useBusinessUnitLabelDisplayName } from 'src/apps/dashboard/helpers/BusinessUnitLabelHelper';
import { useEffectiveFilters } from 'src/apps/dashboard/helpers/FilterHelpers';
import { useBusinessUnitsTableData } from 'src/apps/dashboard/hooks/useBusinessUnitsTableData';
import { useConfigurationFilters } from 'src/apps/dashboard/hooks/useConfigurationFilters';
import { useDashboardDataQuery } from 'src/apps/dashboard/hooks/useDashboardDataQuery';
import { useScoreDataQuery } from 'src/apps/dashboard/hooks/useScoreDataQuery';
import { DashboardTemplate } from 'src/apps/dashboard/templates/DashboardTemplate';
import { isQueryLoading } from 'src/common/helpers/QueryHelpers';
import { useFiltersUrlSync } from 'src/common/hooks/useFiltersUrlSync';
import { usePlatformConfigInitializer } from 'src/common/hooks/usePlatformConfigInitializer';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import { HEADER } from './Dashboard.constants';

export const AccessibilityDashboard: FC = () => {
	usePlatformConfigInitializer();
	const { isUniquenessServiceEnabled } = usePlatformConfigStore();
	const businessUnitDisplayName = useBusinessUnitLabelDisplayName();

	const { filterDefinitions, filterOptions, labelsExcludedFromDashboard } =
		useConfigurationFilters();

	const { filtersFromUrl, handleFilterChange } = useFiltersUrlSync({
		filterDefinitions,
		filterOptions
	});
	const effectiveFilters = useEffectiveFilters(filtersFromUrl, filterDefinitions);

	const dashboardDataQuery = useDashboardDataQuery(
		filtersFromUrl,
		labelsExcludedFromDashboard,
		filterDefinitions
	);
	const {
		data: dashboardData,
		error: dashboardError,
		isLoading: isDashboardLoading
	} = dashboardDataQuery;
	const scoreDataQuery = useScoreDataQuery(
		filtersFromUrl,
		labelsExcludedFromDashboard,
		filterDefinitions
	);
	const { data: scoreData, error: scoreError, isLoading: isScoreLoading } = scoreDataQuery;
	const [businessUnitsTableQuery] = useBusinessUnitsTableData({
		filters: filtersFromUrl,
		filterDefinitions
	});
	const { data: businessUnitsTableData, error: businessUnitsTableError } = businessUnitsTableQuery;

	const isBusinessUnitsLoading = isQueryLoading(businessUnitsTableQuery);

	const filter = useMemo(
		() => ({
			filters: filtersFromUrl,
			filterDefinitions,
			filterOptions,
			onFilterChange: handleFilterChange
		}),
		[filtersFromUrl, filterDefinitions, filterOptions, handleFilterChange]
	);

	const scoreQuery = useMemo(
		() => ({
			data: scoreData,
			isLoading: isScoreLoading,
			error: scoreError
		}),
		[scoreData, isScoreLoading, scoreError]
	);

	const dashboardQuery = useMemo(
		() => ({
			data: dashboardData,
			isLoading: isDashboardLoading,
			error: dashboardError
		}),
		[dashboardData, isDashboardLoading, dashboardError]
	);

	const summaryDashboardQuery = useMemo(
		() => ({
			data: dashboardData
				? {
						...dashboardData,
						uniqueIssues: businessUnitsTableData?.summaryUniqueIssues ?? dashboardData.uniqueIssues,
						totalIssues: businessUnitsTableData?.summaryTotalIssues ?? dashboardData.totalIssues
					}
				: undefined,
			isLoading: isDashboardLoading || isBusinessUnitsLoading,
			error: dashboardError ?? businessUnitsTableError
		}),
		[
			dashboardData,
			isDashboardLoading,
			dashboardError,
			businessUnitsTableData,
			isBusinessUnitsLoading,
			businessUnitsTableError
		]
	);

	const bottom = useMemo(
		() => ({
			kind: 'businessUnitsTable' as const,
			title: businessUnitDisplayName ? pluralize(businessUnitDisplayName) : 'Business Units',
			query: {
				data: businessUnitsTableData,
				isLoading: isBusinessUnitsLoading,
				error: businessUnitsTableError
			}
		}),
		[
			businessUnitDisplayName,
			businessUnitsTableData,
			isBusinessUnitsLoading,
			businessUnitsTableError
		]
	);

	return (
		<DashboardTemplate
			header={HEADER}
			filter={filter}
			effectiveFilters={effectiveFilters}
			scoreQuery={scoreQuery}
			dashboardQuery={dashboardQuery}
			summaryDashboardQuery={summaryDashboardQuery}
			isUniquenessServiceEnabled={isUniquenessServiceEnabled}
			bottom={bottom}
		/>
	);
};
